import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import colors from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {
  useCertifications,
  useDeleteCertification,
  useSingleCertification,
} from "../../Query/useCertifications";
import CertCategoryModal from "./CertCategoryModal";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTranslation } from "react-i18next";

const CertCategoryAll = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("certCategories");
  const { colors: workspace } = useWorkspaceColors();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedCertCategoryId, setSelectedCertCategoryId] = useState<
    number | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { mutate: deleteCertCategory } = useDeleteCertification();

  const { data: certCategories, isLoading, isError } = useCertifications();

  const { data: selecteCertCategory, isLoading: isCertCategoryLoading } =
    useSingleCertification(selectedCertCategoryId ?? 0);

  const handleConfirmationDialogClose = (confirmed: boolean) => {
    setConfirmationDialogOpen(false);
    if (confirmed && selectedCertCategoryId) {
      deleteCertCategory(selectedCertCategoryId);
    }
  };

  const handleEditCertCategory = (certCategoryId: number) => {
    navigate(`/cert_category/edit/${certCategoryId}`);
  };

  const handleCertName = (certCategoryId: number) => {
    setSelectedCertCategoryId(certCategoryId);
    setIsModalOpen(true);
  };

  const handleModalClose = () => setIsModalOpen(false);

  if (isLoading) {
    return (
      <Loading
        backgroundColor={colors.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4, color: colors.red700 }}>
        Error: Failed to fetch Certification Categories list
      </Box>
    );
  }

  const headerCellSx = {
    fontWeight: 600,
    color: colors.gray700,
    backgroundColor: colors.gray50,
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: "0.8rem",
  };

  const bodyCellSx = {
    color: colors.primary_dark,
    fontSize: "0.875rem",
    borderBottom: `1px solid ${colors.gray100}`,
  };

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
    >
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <VerifiedOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("certificationCategories")}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray500 }}>
                  {certCategories?.length ?? 0}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={() => navigate("/cert_category/create")}
              sx={{
                bgcolor: workspace.accent,
                color: workspace.primary_dark,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                gap: 0.75,
                "&:hover": { bgcolor: workspace.accent, opacity: 0.9 },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {t("createNewCertificationCategory")}
              </Typography>
            </IconButton>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <TableContainer
            sx={{
              border: `1px solid ${colors.gray200}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: "70%" }}>
                    {t("name")}
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellSx, width: "30%" }}
                    align="right"
                  >
                    {t("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(certCategories ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{
                        color: colors.gray500,
                        py: 6,
                        backgroundColor: colors.gray50,
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("noCertificationCategories")}
                    </TableCell>
                  </TableRow>
                ) : (
                  (certCategories ?? []).map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{
                        cursor: "pointer",
                        transition: "background-color 0.15s ease-in-out",
                        "&:hover": {
                          backgroundColor: `${workspace.primary_dark}08`,
                        },
                      }}
                    >
                      <TableCell
                        sx={bodyCellSx}
                        onClick={() => handleCertName(item.id)}
                      >
                        {item.name}
                      </TableCell>
                      <TableCell sx={bodyCellSx} align="right">
                        <Tooltip title={t("edit")}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCertCategory(item.id);
                            }}
                            sx={{ color: workspace.accent }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("delete")}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCertCategoryId(item.id);
                              setConfirmationDialogOpen(true);
                            }}
                            sx={{ color: colors.red600 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleConfirmationDialogClose}
        title={t("deleteCertificationCategoryTitle")}
        description={t("deleteCertificationCategoryDescription")}
        content={t("deleteCertificationCategoryContent")}
        actions={[
          {
            label: t("no"),
            onClick: () => handleConfirmationDialogClose(false),
          },
          {
            label: t("yes"),
            onClick: () => handleConfirmationDialogClose(true),
          },
        ]}
      />

      <CertCategoryModal
        open={isModalOpen}
        onClose={handleModalClose}
        certCategory={selecteCertCategory ?? null}
        isLoading={isCertCategoryLoading}
      />
    </Box>
  );
};

export default CertCategoryAll;
